import React, { useState } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

interface LoginProps {
  onLogin: (role: string, userData: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010';
      console.log('Attempting to connect to backend at:', backendUrl);
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pin }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store user info and token in localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);
        
        // Call onLogin immediately - App.tsx will handle org selection
        onLogin(data.user.role, data.user);
        setError('');
      } else {
        setError(data.error || 'Invalid PIN. Please try again.');
        setPin('');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Unable to connect to server. Please try again.');
      setPin('');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 5,
            width: '100%',
            textAlign: 'center',
            borderRadius: 3,
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <img 
              src={`${process.env.PUBLIC_URL}/logos/SONIQPulseNight.png`}
              alt="SONIQ Pulse Logo"
              style={{ 
                maxHeight: '80px',
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                marginBottom: '32px',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))',
                objectFit: 'contain'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: '#2C3E50',
                mb: 1,
              }}
            >
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Enter your PIN to access the dashboard
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Enter PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              variant="outlined"
              autoFocus
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '4px' }
              }}
              sx={{ mb: 3 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!pin}
              sx={{
                backgroundColor: '#4B88A2',
                '&:hover': {
                  backgroundColor: '#3A6D82',
                },
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              Login
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
